import { Player } from './player';
import { isFloorball, Sport } from './sport';

export enum PlayerType {
  KEEPER = 'keeper',
  DEFENDER = 'defender',
  FORWARD = 'forward'
}

export enum GameStatus {
  NEW = 'new',
  IN_COLLECTION = 'in_collection',
  IN_EXTENDED_COLLECTION = 'in_extended_collection',
  QUALITY_CONTROL = 'quality_control',
  COMPLETE = 'complete',
  FAILED = 'failed',
  ABANDONED = 'abandoned'
}

export enum AggregationStatus {
  IN_PROGRESS = 'in_progress',
  COMPLETE = 'complete',
  FAILED = 'failed'
}

export enum FinalizationStatus {
  IN_PROGRESS = 'in_progress',
  COMPLETE = 'complete',
  FAILED = 'failed'
}

export enum SeasonType {
  REGULAR = 'Regular',
  PLAYOFF = 'Playoff',
  PLAYOUT = 'Playout',
  EXHIBITION = 'Exhibition',
  OTHER = 'Other'
}

export enum IdType {
  MASTER = 'Master',
  SIHF = 'SIHF',
  ELITE_PROSPECTS = 'EliteProspects',
  INSTAT = 'InStat',
  NHL = 'NHL'
}

export enum DataSet {
  LITE = 'LITE',
  LITE_PLUS = 'LITE_PLUS',
  FULL = 'FULL'
}

export enum OfficiatingDataSet {
  LITE = 'LITE',
  FULL = 'FULL'
}

export enum SihfGameStatus {
  BEFORE_GAME = 0,
  PERIOD_1_RUNNING = 1,
  BREAK_1_2 = 2,
  PERIOD_2_RUNNING = 3,
  BREAK_2_3 = 4,
  PERIOD_3_RUNNING = 5,
  PERIOD_3_FINISHED = 6,
  OVERTIME = 7,
  SHOOTOUT = 8,
  END = 9,
  END_AFTER_OVERTIME = 10,
  END_AFTER_SHOOTOUT = 11,
  GAME_COMPLETE = 12,
  GAME_POSTPONED = 13,
  GAME_ABORTED = 14, // "Spiel abgebrochen"; was GAME_CANCELLED until 2026-09-27
  FORFEIT_HOME = 15,
  FORFEIT_AWAY = 16,
  ZERO_POINTS = 17,
  GAME_CANCELLED = 18 // "Abgesagt"
}

export enum GameProgress {
  BEFORE_START = 'before_start',
  IN_PROGRESS = 'in_progress',
  ENDED = 'ended'
}

export enum CameraAngle {
  MAIN = 'main',
  TV_FEED = 'tvfeed',
  MULTIVIEW = 'multiview',
  BEHIND_GOAL_1 = 'behindgoal1',
  BEHIND_GOAL_2 = 'behindgoal2',
  OVER_GOAL_1 = 'overgoal1',
  OVER_GOAL_2 = 'overgoal2',
  CORNER_1 = 'corner1',
  CORNER_2 = 'corner2',
  CLOSE_UP = 'closeup',
  GAME_CLOCK = 'gameclock',
  PANORAMIC = 'panoramic',
  FALLBACK = 'fallback'
}

export enum VideoVariantEnum {
  LIVERECORDING = 'liverecording',
  CLIPPED = 'clipped'
}

export enum VideoClipsStatus {
  OPEN = 'open',
  CREATING_VIDEO = 'creating_video',
  COMPLETED = 'completed',
  ERROR = 'error'
}

export type VideoVariantType = `${VideoVariantEnum}`;
export type VideoFormat = 'mp4' | 'hls' | 'dash' | 'webm';

export interface Video {
  id: string;
  cameraAngle?: CameraAngle;
  variant?: VideoVariantType;
  format?: VideoFormat;

  url?: string;
  urlSigned?: string;
  downloadStatus?: 'new' | 'in_progress' | 'complete' | 'error';
  downloadError?: string;
  frameRate?: number;
  trackOffset?: number; // cv tracking video offset

  /** number of seconds the video is delayed compared to the first video */
  offset?: number;

  /** start time of the live stream in seconds */
  startTime?: number;

  /** media server stream identifier */
  streamId?: string;

  /** temporary attributes */
  mediaId?: string;
  index?: number;
}

export interface AuditLogEntry {
  action: string;
  date: string;
  user: string;
}

export interface EventStats {
  _id: {
    eventType: string;
    gameId: string;
  };
  start: number;
  end: number;
  count: number;
  progressPercentage: number;
}

export interface CVPipelineStatus {
  taskArn: string;
  jobId: string;
  period: string;
  phase: string;
  priority?: string;
  liveMode: boolean;
  createdAt?: Date;
  startedAt?: Date;
  stoppedAt?: Date;
  start: Date;
  stop?: Date;
  createdBy?: string;
  lastStatus?:
    | 'SUBMITTED'
    | 'PENDING'
    | 'RUNNABLE'
    | 'STARTING'
    | 'RUNNING'
    | 'SUCCEEDED'
    | 'FAILED';
  exitCode?: number;
  version?: string;
  warnings: [{ key: string; description: string }];
}

export class GameOfficial {
  public officialId?: number;
  public unknownOfficialInfo?: string;
  public officialRoleInGame: OfficialRoleInGame;
  public sequenceNumber: number;
  public firstName?: string;
  public lastName?: string;
  public jerseyNumber?: number;
}

export interface GameOfficialInputField {
  label: string;
  role: OfficialRoleInGame;
  sequenceNumber: number;
}

export type OfficialRoleInGame = 'head' | 'linesperson';

// outcome of the last TOI delivery to the Stathletes exchange bucket, recorded
// on the game by the server (see export-stathletes.service.js)
export interface StathletesExport {
  // absent until the first delivery attempt of the game
  status?: 'success' | 'failed';
  // when the last attempt finished, successful or not; everything from bucket
  // down describes the last successful delivery and survives a later failure
  finishedAt?: string;
  exportedBy?: string | null;
  bucket?: string;
  folder?: string;
  exchangeGameId?: string;
  deliveryVersion?: number;
  playerCount?: number;
  playersWithoutDobCount?: number;
  shiftCount?: number;
  openShiftCount?: number;
  videoCount?: number;
  error?: string | null;
  // set while the game waits for the export job, which delivers it within
  // about a minute and then clears it
  requestedAt?: string;
  requestedBy?: string | null;
  // a single-game request, delivered ahead of a season backlog
  prioritized?: boolean;
}

export interface StathletesExportRequest {
  requestedAt: string;
  requestedBy: string | null;
}

// narrows a league and season TOI delivery
export interface StathletesSeasonFilters {
  seasonType?: SeasonType | null;
  // only games without a successful delivery yet
  undeliveredOnly?: boolean;
}

// the completed games of a league and season a TOI delivery would export
export interface StathletesSeasonPreview {
  league: string;
  season: string;
  seasonType: SeasonType | null;
  undeliveredOnly: boolean;
  gameCount: number;
  games: {
    gameId: string;
    date: string;
    venueUTCOffset: string | null;
    homeTeam: string;
    awayTeam: string;
    lastExportStatus: 'success' | 'failed' | null;
    deliveryVersion: number | null;
  }[];
}

// outcome of a TOI delivery of all completed games of a league and season
export interface StathletesSeasonExport {
  league: string;
  season: string;
  seasonType: SeasonType | null;
  undeliveredOnly: boolean;
  // games flagged for the export job
  requestedCount: number;
  // confirmed games no longer in scope (e.g. reopened since the preview)
  skippedCount: number;
}

/**
 * Where a customer provides this game's video, as picked in Data Cockpit
 * (INT-6977). Read-only here: the collector shows it, the customer owns it.
 */
export interface GameVideoSource {
  publicId: string;
  // slug of the customer (tenant) the source belongs to
  tenant: string;
  // which side of the game owns the source, or null when the source's team is
  // no longer one of the game's teams
  team: 'home' | 'away' | null;
  // 'External platform' / 'Direct upload', worded as Data Cockpit words it
  platformLabel: string;
  // the name the customer gave the platform (Live Barn, a club portal, ...);
  // null for platforms they don't name themselves
  label: string | null;
  notes: string | null;
  loginUrl: string | null;
  // whether fetching the video means signing in at all, and whether a login has
  // been stored yet: both false-y is a source still waiting for its credentials
  needsCredentials: boolean;
  hasCredentials: boolean;
  // where the login is stored, for users whose token carries
  // 'read:video-sources'; absent for everyone else
  secretUrl?: string | null;
  // whether the customer picked this source for this game; false means it is
  // one they only maintain for the season. Decided per team
  selected: boolean;
}

const FLOORBALL_OVERTIME_LENGTH_IN_MINUTES = 10;

export class Game {
  public _id: string;
  public gameId: string; // SIHF game ID filled by Reporter API
  public league: string;
  // Resolved from the league by the server on read, never stored. Missing
  // (new or older responses) counts as ice hockey.
  public sport?: Sport;
  public date: string;
  public periodLengthInMinutes: number; // Period length in minutes
  public overtimeLengthInMinutes?: number; // set per game; see overtimeLength
  public venueTimezoneId?: string;
  public venueUTCOffset?: string;
  public venueName?: string;
  public videos?: Video[] = [];
  public season: string;
  public seasonType: SeasonType;
  public homeTeam: string;
  public homeTeamId: string;
  public awayTeam: string;
  public awayTeamId: string;
  public homeGoals: number;
  public awayGoals: number;
  public homePoints?: number | null;
  public awayPoints?: number | null;
  public pointSystemAvailable?: boolean;
  public overtime: boolean;
  public shootout: boolean;

  public isCVProcessed: boolean; // whether cv-pipeline has been run
  // set by the server when the game was created: whether the home team was a CV
  // collection candidate then. null when the lookup could not be made.
  public isCVCandidate?: boolean | null;
  public homeTeamStartPosition?: 'left' | 'right' = 'left';
  public cvPipeline?: CVPipelineStatus[] = [];
  public topScorerIds?: string[] = [];

  public homeTeamColor?: string; // hex code
  public awayTeamColor?: string; // hex code

  public eventCollectorName: string;
  public tags: string[];

  // status fields
  public progress: GameProgress;
  public status: GameStatus = GameStatus.NEW;
  public aggregationStatus?: AggregationStatus;
  public aggregationTaskArn?: string;
  public aggregationStartDate?: string;
  public aggregationEndDate?: string;
  public finalization: {
    start?: Date;
    status: FinalizationStatus;
    commandId?: string;
    instanceId?: string;
  };
  public hasWarnings: boolean;
  public hasErrors: boolean;

  public goalClipsStatus?: VideoClipsStatus;
  public isLiveCollection: boolean;
  public isTrainingGame: boolean;
  public isSihfLive: boolean;
  public lagClock: boolean;
  public autoPuckPossessionEvents: boolean;
  public disableUpdates: boolean;
  public auditLog: AuditLogEntry[] = [];
  public lastSihfExportId: number;
  public idType: IdType;
  public dataSet: DataSet;
  public officiatingDataSet?: OfficiatingDataSet;
  public sihfStatus: SihfGameStatus;
  public isHighlightCollection: boolean;
  public goalClips: boolean;
  public isRealtimeCollection: boolean;
  public isCVLiveFOINTCollection: boolean;
  public isCameraTimeSynchronized?: boolean; // whether the timing of all cameras has been synced
  public isAutoCameraSync?: boolean; // whether camera offsets are synced automatically while the game is live
  public isExportVideoReviewClips: boolean;
  public exportVideoReviewClipsStatus?: VideoClipsStatus;
  public stathletesExport?: StathletesExport;
  public isAssistsCollection: boolean;
  public isVideoReview?: boolean;
  public isGameTimeImport?: boolean; // whether SIHF game time events should be inserted as events
  public enableExtraLines?: boolean; // enables sixth, seventh, eighth lines and related tools
  public isSwapLineupPlayersLocked?: boolean; // INT-6527: is swapping lineup positions locked TOI view

  // customer provided lineup from customer_data.game_lineup
  public customerLineup?: {
    home?: Record<string, string>;
    away?: Record<string, string>;
  };
  // set by the server when the customer lineup lookup failed (as opposed to
  // there being no lineup), so the UI can tell the operator to retry
  public customerLineupUnavailable?: boolean;

  // pending schedule correction from customer_data.game_schedule_correction
  public pendingScheduleCorrection?: {
    correctionDate?: string | null;
    correctionVenueName?: string | null;
    submittedAt?: string;
  };
  // set by the server when the pending-correction lookup failed (as opposed to
  // there being no correction), so the UI can tell the operator to retry
  public pendingScheduleCorrectionUnavailable?: boolean;

  // video sources the customer picked for this game in Data Cockpit
  // (customer_data.game_schedule -> customer_data.team_video_source). One entry
  // per tenant: the home and away teams may belong to different customers.
  public videoSources?: GameVideoSource[];
  // set by the server when the video-source lookup failed (as opposed to no
  // source having been picked), so the UI can tell the operator to retry
  public videoSourcesUnavailable?: boolean;

  // home team
  // goalkeepers
  public homeGoalkeeper: string;
  public homeGoalkeeperID: string;
  public homeBackupGoalkeeper: string;
  public homeBackupGoalkeeperID: string;
  // first line
  public homeFirstLineLeftDefence: string;
  public homeFirstLineLeftDefenceID: string;
  public homeFirstLineRightDefence: string;
  public homeFirstLineRightDefenceID: string;
  public homeFirstLineCenter: string;
  public homeFirstLineCenterID: string;
  public homeFirstLineLeftForward: string;
  public homeFirstLineLeftForwardID: string;
  public homeFirstLineRightForward: string;
  public homeFirstLineRightForwardID: string;
  // second line
  public homeSecondLineLeftDefence: string;
  public homeSecondLineLeftDefenceID: string;
  public homeSecondLineRightDefence: string;
  public homeSecondLineRightDefenceID: string;
  public homeSecondLineCenter: string;
  public homeSecondLineCenterID: string;
  public homeSecondLineLeftForward: string;
  public homeSecondLineLeftForwardID: string;
  public homeSecondLineRightForward: string;
  public homeSecondLineRightForwardID: string;
  // third line
  public homeThirdLineLeftDefence: string;
  public homeThirdLineLeftDefenceID: string;
  public homeThirdLineRightDefence: string;
  public homeThirdLineRightDefenceID: string;
  public homeThirdLineCenter: string;
  public homeThirdLineCenterID: string;
  public homeThirdLineLeftForward: string;
  public homeThirdLineLeftForwardID: string;
  public homeThirdLineRightForward: string;
  public homeThirdLineRightForwardID: string;
  // fourth line
  public homeFourthLineLeftDefence: string;
  public homeFourthLineLeftDefenceID: string;
  public homeFourthLineRightDefence: string;
  public homeFourthLineRightDefenceID: string;
  public homeFourthLineCenter: string;
  public homeFourthLineCenterID: string;
  public homeFourthLineLeftForward: string;
  public homeFourthLineLeftForwardID: string;
  public homeFourthLineRightForward: string;
  public homeFourthLineRightForwardID: string;
  // fifth line - filled automatically
  public homeFifthLineLeftDefence: string;
  public homeFifthLineLeftDefenceID: string;
  public homeFifthLineRightDefence: string;
  public homeFifthLineRightDefenceID: string;
  public homeFifthLineCenter: string;
  public homeFifthLineCenterID: string;
  public homeFifthLineLeftForward: string;
  public homeFifthLineLeftForwardID: string;
  public homeFifthLineRightForward: string;
  public homeFifthLineRightForwardID: string;
  // sixth line - filled automatically
  public homeSixthLineLeftDefence: string;
  public homeSixthLineLeftDefenceID: string;
  public homeSixthLineRightDefence: string;
  public homeSixthLineRightDefenceID: string;
  public homeSixthLineCenter: string;
  public homeSixthLineCenterID: string;
  public homeSixthLineLeftForward: string;
  public homeSixthLineLeftForwardID: string;
  public homeSixthLineRightForward: string;
  public homeSixthLineRightForwardID: string;
  // seventh line - filled automatically
  public homeSeventhLineLeftDefence: string;
  public homeSeventhLineLeftDefenceID: string;
  public homeSeventhLineRightDefence: string;
  public homeSeventhLineRightDefenceID: string;
  public homeSeventhLineCenter: string;
  public homeSeventhLineCenterID: string;
  public homeSeventhLineLeftForward: string;
  public homeSeventhLineLeftForwardID: string;
  public homeSeventhLineRightForward: string;
  public homeSeventhLineRightForwardID: string;
  // eighth line - filled automatically
  public homeEighthLineLeftDefence: string;
  public homeEighthLineLeftDefenceID: string;
  public homeEighthLineRightDefence: string;
  public homeEighthLineRightDefenceID: string;
  public homeEighthLineCenter: string;
  public homeEighthLineCenterID: string;
  public homeEighthLineLeftForward: string;
  public homeEighthLineLeftForwardID: string;
  public homeEighthLineRightForward: string;
  public homeEighthLineRightForwardID: string;
  // extra line - only filled manually
  public homeExtraLineDefence: string;
  public homeExtraLineDefenceID: string;
  public homeExtraLineForward: string;
  public homeExtraLineForwardID: string;

  // away team
  // goalkeepers
  public awayGoalkeeper: string;
  public awayGoalkeeperID: string;
  public awayBackupGoalkeeper: string;
  public awayBackupGoalkeeperID: string;
  // first line
  public awayFirstLineLeftDefence: string;
  public awayFirstLineLeftDefenceID: string;
  public awayFirstLineRightDefence: string;
  public awayFirstLineRightDefenceID: string;
  public awayFirstLineCenter: string;
  public awayFirstLineCenterID: string;
  public awayFirstLineLeftForward: string;
  public awayFirstLineLeftForwardID: string;
  public awayFirstLineRightForward: string;
  public awayFirstLineRightForwardID: string;
  // second line
  public awaySecondLineLeftDefence: string;
  public awaySecondLineLeftDefenceID: string;
  public awaySecondLineRightDefence: string;
  public awaySecondLineRightDefenceID: string;
  public awaySecondLineCenter: string;
  public awaySecondLineCenterID: string;
  public awaySecondLineLeftForward: string;
  public awaySecondLineLeftForwardID: string;
  public awaySecondLineRightForward: string;
  public awaySecondLineRightForwardID: string;
  // third line
  public awayThirdLineLeftDefence: string;
  public awayThirdLineLeftDefenceID: string;
  public awayThirdLineRightDefence: string;
  public awayThirdLineRightDefenceID: string;
  public awayThirdLineCenter: string;
  public awayThirdLineCenterID: string;
  public awayThirdLineLeftForward: string;
  public awayThirdLineLeftForwardID: string;
  public awayThirdLineRightForward: string;
  public awayThirdLineRightForwardID: string;
  // fourth line
  public awayFourthLineLeftDefence: string;
  public awayFourthLineLeftDefenceID: string;
  public awayFourthLineRightDefence: string;
  public awayFourthLineRightDefenceID: string;
  public awayFourthLineCenter: string;
  public awayFourthLineCenterID: string;
  public awayFourthLineLeftForward: string;
  public awayFourthLineLeftForwardID: string;
  public awayFourthLineRightForward: string;
  public awayFourthLineRightForwardID: string;
  // fifth line - filled automatically
  public awayFifthLineLeftDefence: string;
  public awayFifthLineLeftDefenceID: string;
  public awayFifthLineRightDefence: string;
  public awayFifthLineRightDefenceID: string;
  public awayFifthLineCenter: string;
  public awayFifthLineCenterID: string;
  public awayFifthLineLeftForward: string;
  public awayFifthLineLeftForwardID: string;
  public awayFifthLineRightForward: string;
  public awayFifthLineRightForwardID: string;
  // sixth line - filled automatically
  public awaySixthLineLeftDefence: string;
  public awaySixthLineLeftDefenceID: string;
  public awaySixthLineRightDefence: string;
  public awaySixthLineRightDefenceID: string;
  public awaySixthLineCenter: string;
  public awaySixthLineCenterID: string;
  public awaySixthLineLeftForward: string;
  public awaySixthLineLeftForwardID: string;
  public awaySixthLineRightForward: string;
  public awaySixthLineRightForwardID: string;
  // seventh line - filled automatically
  public awaySeventhLineLeftDefence: string;
  public awaySeventhLineLeftDefenceID: string;
  public awaySeventhLineRightDefence: string;
  public awaySeventhLineRightDefenceID: string;
  public awaySeventhLineCenter: string;
  public awaySeventhLineCenterID: string;
  public awaySeventhLineLeftForward: string;
  public awaySeventhLineLeftForwardID: string;
  public awaySeventhLineRightForward: string;
  public awaySeventhLineRightForwardID: string;
  // eighth line - filled automatically
  public awayEighthLineLeftDefence: string;
  public awayEighthLineLeftDefenceID: string;
  public awayEighthLineRightDefence: string;
  public awayEighthLineRightDefenceID: string;
  public awayEighthLineCenter: string;
  public awayEighthLineCenterID: string;
  public awayEighthLineLeftForward: string;
  public awayEighthLineLeftForwardID: string;
  public awayEighthLineRightForward: string;
  public awayEighthLineRightForwardID: string;
  // extra line - only filled manually
  public awayExtraLineDefence: string;
  public awayExtraLineDefenceID: string;
  public awayExtraLineForward: string;
  public awayExtraLineForwardID: string;

  public officials: GameOfficial[] = [];

  public eventStats?: EventStats[];

  constructor(obj: any) {
    Object.assign(this, obj);
  }

  // Overtime length in minutes: the game's own setting, else floorball's
  // 10 minutes (IFF Rules 2026, 203). Undefined keeps each clock's older
  // default, so ice hockey games without a setting show what they always did.
  get overtimeLength(): number | undefined {
    return (
      this.overtimeLengthInMinutes ??
      (isFloorball(this.sport)
        ? FLOORBALL_OVERTIME_LENGTH_IN_MINUTES
        : undefined)
    );
  }

  hasOfficiatingCollection(): boolean {
    return (
      this.officiatingDataSet === OfficiatingDataSet.LITE ||
      this.officiatingDataSet === OfficiatingDataSet.FULL
    );
  }

  getTeams() {
    return [
      { id: this.homeTeamId, shortName: this.homeTeam },
      { id: this.awayTeamId, shortName: this.awayTeam }
    ];
  }

  validPlayerPredicate(player: string) {
    return player != null && player !== 'n/a' && player.trim() !== '';
  }

  getOpposingTeam(team: string): string {
    if (team === this.homeTeam) {
      return this.awayTeam;
    } else if (team === this.awayTeam) {
      return this.homeTeam;
    } else {
      throw new Error('Unknown team: ' + team);
    }
  }

  getGoalkeepers(team: string): string[] {
    if (team === this.homeTeam) {
      return this.getHomeTeamGoalkeepers();
    } else if (team === this.awayTeam) {
      return this.getAwayTeamGoalkeepers();
    } else {
      throw new Error('Unknown team: ' + team);
    }
  }

  getDefensemen(team: string): string[] {
    if (team === this.homeTeam) {
      return this.getHomeTeamDefensemen();
    } else if (team === this.awayTeam) {
      return this.getAwayTeamDefensemen();
    } else {
      throw new Error('Unknown team: ' + team);
    }
  }

  getForwards(team: string): string[] {
    if (team === this.homeTeam) {
      return this.getHomeTeamForwards();
    } else if (team === this.awayTeam) {
      return this.getAwayTeamForwards();
    } else {
      throw new Error('Unknown team: ' + team);
    }
  }

  getCenters(team: string): string[] {
    if (team === this.homeTeam) {
      return this.getHomeTeamCenters();
    } else if (team === this.awayTeam) {
      return this.getAwayTeamCenters();
    } else {
      throw new Error('Unknown team: ' + team);
    }
  }

  getHomeTeamCenters(): string[] {
    return [
      this.homeFirstLineCenter,
      this.homeSecondLineCenter,
      this.homeThirdLineCenter,
      this.homeFourthLineCenter,
      this.homeFifthLineCenter,
      this.homeSixthLineCenter,
      this.homeSeventhLineCenter,
      this.homeEighthLineCenter
    ].map((player) => (this.validPlayerPredicate(player) ? player : null));
  }

  getAwayTeamCenters(): string[] {
    return [
      this.awayFirstLineCenter,
      this.awaySecondLineCenter,
      this.awayThirdLineCenter,
      this.awayFourthLineCenter,
      this.awayFifthLineCenter,
      this.awaySixthLineCenter,
      this.awaySeventhLineCenter,
      this.awayEighthLineCenter
    ].map((player) => (this.validPlayerPredicate(player) ? player : null));
  }

  getHomeTeamGoalkeepers(): string[] {
    return [this.homeGoalkeeper, this.homeBackupGoalkeeper];
  }

  getAwayTeamGoalkeepers(): string[] {
    return [this.awayGoalkeeper, this.awayBackupGoalkeeper];
  }

  getHomeTeamDefensemen(): string[] {
    return [
      this.homeFirstLineLeftDefence,
      this.homeFirstLineRightDefence,
      this.homeSecondLineLeftDefence,
      this.homeSecondLineRightDefence,
      this.homeThirdLineLeftDefence,
      this.homeThirdLineRightDefence,
      this.homeFourthLineLeftDefence,
      this.homeFourthLineRightDefence,
      this.homeFifthLineLeftDefence,
      this.homeFifthLineRightDefence,
      this.homeSixthLineLeftDefence,
      this.homeSixthLineRightDefence,
      this.homeSeventhLineLeftDefence,
      this.homeSeventhLineRightDefence,
      this.homeEighthLineLeftDefence,
      this.homeEighthLineRightDefence,
      this.homeExtraLineDefence
    ].map((player) => (this.validPlayerPredicate(player) ? player : null));
  }

  getHomeTeamForwards(): string[] {
    return [
      this.homeFirstLineLeftForward,
      this.homeFirstLineCenter,
      this.homeFirstLineRightForward,
      this.homeSecondLineLeftForward,
      this.homeSecondLineCenter,
      this.homeSecondLineRightForward,
      this.homeThirdLineLeftForward,
      this.homeThirdLineCenter,
      this.homeThirdLineRightForward,
      this.homeFourthLineLeftForward,
      this.homeFourthLineCenter,
      this.homeFourthLineRightForward,
      this.homeFifthLineLeftForward,
      this.homeFifthLineCenter,
      this.homeFifthLineRightForward,
      this.homeSixthLineLeftForward,
      this.homeSixthLineCenter,
      this.homeSixthLineRightForward,
      this.homeSeventhLineLeftForward,
      this.homeSeventhLineCenter,
      this.homeSeventhLineRightForward,
      this.homeEighthLineLeftForward,
      this.homeEighthLineCenter,
      this.homeEighthLineRightForward,
      this.homeExtraLineForward
    ].map((player) => (this.validPlayerPredicate(player) ? player : null));
  }

  getAwayTeamDefensemen(): string[] {
    return [
      this.awayFirstLineLeftDefence,
      this.awayFirstLineRightDefence,
      this.awaySecondLineLeftDefence,
      this.awaySecondLineRightDefence,
      this.awayThirdLineLeftDefence,
      this.awayThirdLineRightDefence,
      this.awayFourthLineLeftDefence,
      this.awayFourthLineRightDefence,
      this.awayFifthLineLeftDefence,
      this.awayFifthLineRightDefence,
      this.awaySixthLineLeftDefence,
      this.awaySixthLineRightDefence,
      this.awaySeventhLineLeftDefence,
      this.awaySeventhLineRightDefence,
      this.awayEighthLineLeftDefence,
      this.awayEighthLineRightDefence,
      this.awayExtraLineDefence
    ].map((player) => (this.validPlayerPredicate(player) ? player : null));
  }

  getAwayTeamForwards(): string[] {
    return [
      this.awayFirstLineLeftForward,
      this.awayFirstLineCenter,
      this.awayFirstLineRightForward,
      this.awaySecondLineLeftForward,
      this.awaySecondLineCenter,
      this.awaySecondLineRightForward,
      this.awayThirdLineLeftForward,
      this.awayThirdLineCenter,
      this.awayThirdLineRightForward,
      this.awayFourthLineLeftForward,
      this.awayFourthLineCenter,
      this.awayFourthLineRightForward,
      this.awayFifthLineLeftForward,
      this.awayFifthLineCenter,
      this.awayFifthLineRightForward,
      this.awaySixthLineLeftForward,
      this.awaySixthLineCenter,
      this.awaySixthLineRightForward,
      this.awaySeventhLineLeftForward,
      this.awaySeventhLineCenter,
      this.awaySeventhLineRightForward,
      this.awayEighthLineLeftForward,
      this.awayEighthLineCenter,
      this.awayEighthLineRightForward,
      this.awayExtraLineForward
    ].map((player) => (this.validPlayerPredicate(player) ? player : null));
  }

  getHomeTeamPlayers(): string[] {
    let homeTeamPlayers = [this.homeGoalkeeper, this.homeBackupGoalkeeper];
    homeTeamPlayers = homeTeamPlayers.concat(this.getHomeTeamDefensemen());
    homeTeamPlayers = homeTeamPlayers.concat(this.getHomeTeamForwards());
    return homeTeamPlayers.filter(
      (player) => player != null && player !== 'n/a' && player.trim() !== ''
    );
  }

  getAwayTeamPlayers(): string[] {
    let awayTeamPlayers = [this.awayGoalkeeper, this.awayBackupGoalkeeper];
    awayTeamPlayers = awayTeamPlayers.concat(this.getAwayTeamDefensemen());
    awayTeamPlayers = awayTeamPlayers.concat(this.getAwayTeamForwards());
    return awayTeamPlayers.filter(
      (player) => player != null && player !== 'n/a' && player.trim() !== ''
    );
  }

  getAllPlayers(): string[] {
    return [...this.getHomeTeamPlayers(), ...this.getAwayTeamPlayers()];
  }

  getPlayersByTeam(team: string): string[] {
    if (team === this.homeTeam) {
      return this.getHomeTeamPlayers();
    } else if (team === this.awayTeam) {
      return this.getAwayTeamPlayers();
    } else {
      throw new Error('Unknown team: ' + team);
    }
  }

  getAllPlayersObj(): Player[] {
    return [
      ...this.getPlayersAsObj(this.homeTeam),
      ...this.getPlayersAsObj(this.awayTeam)
    ];
  }

  getPlayersAsObj(team: string): Player[] {
    const players =
      team === this.homeTeam
        ? this.getHomeTeamPlayers()
        : this.getAwayTeamPlayers();
    return players
      .filter((p) => p && p !== 'n/a')
      .map((p) => {
        try {
          return this.getPlayerObj(p, team);
        } catch (err) {
          console.error('Failed to getPlayerObj: ' + err.message);
          return null;
        }
      });
  }

  getPlayerObj(playerNumber: string, team: string): Player {
    const teamId = team === this.homeTeam ? this.homeTeamId : this.awayTeamId;

    if (
      !playerNumber ||
      playerNumber === 'n/a' ||
      playerNumber.indexOf('-') === -1
    ) {
      return {
        team,
        teamId
      } as Player;
    }
    const homeTeamOrAwayTeam = team === this.homeTeam;
    const jerseyNumber = parseInt(playerNumber.split('-')[0].trim(), 10);
    const playerId = this.getPlayerIdByJerseyNumber(
      homeTeamOrAwayTeam,
      playerNumber
    );
    const name = playerNumber.split('-')[1].trim();
    const position = this.getPlayerPosition(playerNumber, team);
    const positionIndex = this.getPositionIndex(playerNumber, team);
    const line = this.getLine(playerNumber, team);
    const key = Object.keys(this).find((k) => this[k] === playerNumber);

    return {
      playerId,
      jerseyNumber,
      key,
      name,
      team,
      teamId,
      position,
      positionIndex,
      line,
      playerNumber
    } as Player;
  }

  private getLine(playerNumber: string, team: string) {
    const playerType = this.getPlayerType(playerNumber, team);
    if (playerType === PlayerType.DEFENDER) {
      return 1 + Math.floor(this.getDefensemen(team).indexOf(playerNumber) / 2);
    } else if (playerType === PlayerType.FORWARD) {
      return 1 + Math.floor(this.getForwards(team).indexOf(playerNumber) / 3);
    } else if (playerType === PlayerType.KEEPER) {
      return this.getGoalkeepers(team).indexOf(playerNumber) + 1;
    } else {
      console.warn('Cannot find line index for player type: ' + playerType);
      return -1;
    }
  }

  private getPositionIndex(playerNumber: string, team: string) {
    const playerType = this.getPlayerType(playerNumber, team);

    if (playerType === PlayerType.DEFENDER) {
      const positionMap = {
        Left: 0,
        Right: 1
      };
      return this.mapFieldToPositionKey(playerNumber, positionMap);
    } else if (playerType === PlayerType.FORWARD) {
      const positionMap = {
        Left: 0,
        Center: 1,
        Right: 2
      };
      return this.mapFieldToPositionKey(playerNumber, positionMap);
    } else if (playerType === PlayerType.KEEPER) {
      return 0;
    } else {
      console.warn('Cannot find line index for player type: ' + playerType);
      return -1;
    }
  }

  private mapFieldToPositionKey(playerNumber: string, positionMap: any) {
    // inspects all fields of the game object to find the player number
    for (const key in this) {
      if (this[key] === playerNumber) {
        const position = Object.keys(positionMap).find((pos) =>
          key.includes(pos)
        );
        if (!position) {
          console.warn('Could not find position for player ' + playerNumber);
          return null;
        }
        return positionMap[position];
      }
    }
  }

  getPlayerType(playerNumber: string, team: string): PlayerType {
    if (team === this.homeTeam) {
      if (this.getHomeTeamDefensemen().indexOf(playerNumber) > -1) {
        return PlayerType.DEFENDER;
      } else if (this.getHomeTeamForwards().indexOf(playerNumber) > -1) {
        return PlayerType.FORWARD;
      } else if (
        this.homeGoalkeeper === playerNumber ||
        this.homeBackupGoalkeeper === playerNumber
      ) {
        return PlayerType.KEEPER;
      } else {
        console.warn(
          'Could not find type for player ' + playerNumber + ' in team ' + team
        );
        return null;
      }
    } else {
      if (this.getAwayTeamDefensemen().indexOf(playerNumber) > -1) {
        return PlayerType.DEFENDER;
      } else if (this.getAwayTeamForwards().indexOf(playerNumber) > -1) {
        return PlayerType.FORWARD;
      } else if (
        this.awayGoalkeeper === playerNumber ||
        this.awayBackupGoalkeeper === playerNumber
      ) {
        return PlayerType.KEEPER;
      } else {
        console.warn(
          'Could not find type for player ' + playerNumber + ' in team ' + team
        );
        return null;
      }
    }
  }

  getPlayerPosition(playerNumber: string, team: string): string {
    const g = this.getGoalkeepers(team).indexOf(playerNumber);
    if (g > -1) {
      return 'goalkeeper';
    }
    const d = this.getDefensemen(team).indexOf(playerNumber);
    if (d > -1) {
      return 'defender';
    }
    const f = this.getForwards(team).indexOf(playerNumber);
    if (f > -1) {
      return 'forward';
    }
    throw new Error('Could not find position for player: ' + playerNumber);
  }

  getPlayerPositionNameAndIndex(playerNumber: string, team: string): string {
    const g = this.getGoalkeepers(team).indexOf(playerNumber);
    if (g > -1) {
      return 'goalkeeper';
    }
    const d = this.getDefensemen(team).indexOf(playerNumber);
    if (d > -1) {
      const pos = ['left', 'right'];
      return 'defender-' + pos[d % 2];
    }
    const f = this.getForwards(team).indexOf(playerNumber);
    if (f > -1) {
      const pos = ['left', 'center', 'right'];
      return 'forward-' + pos[f % 3];
    }
    throw new Error('Could not find position for player: ' + playerNumber);
  }

  getPlayerIdByJerseyNumber(
    homeOrWayTeam: boolean,
    playerNumber: string
  ): string {
    const players = homeOrWayTeam
      ? this.getHomeTeamPlayersById()
      : this.getAwayTeamPlayersById();
    const result = players
      .filter((t) => t[0] != null && t[1] != null)
      .filter((player) => player[1] === playerNumber);
    if (result.length === 1) {
      return result[0][0];
    } else {
      return null;
    }
  }

  hasLineup() {
    return (
      this.getAllPlayers().length > 0 &&
      this.homeGoalkeeperID &&
      this.awayGoalkeeperID
    );
  }

  private getHomeTeamPlayersById(): [string, string][] {
    return [
      [this.homeGoalkeeperID, this.homeGoalkeeper],
      [this.homeBackupGoalkeeperID, this.homeBackupGoalkeeper],
      [this.homeFirstLineLeftDefenceID, this.homeFirstLineLeftDefence],
      [this.homeFirstLineRightDefenceID, this.homeFirstLineRightDefence],
      [this.homeSecondLineLeftDefenceID, this.homeSecondLineLeftDefence],
      [this.homeSecondLineRightDefenceID, this.homeSecondLineRightDefence],
      [this.homeThirdLineLeftDefenceID, this.homeThirdLineLeftDefence],
      [this.homeThirdLineRightDefenceID, this.homeThirdLineRightDefence],
      [this.homeFourthLineLeftDefenceID, this.homeFourthLineLeftDefence],
      [this.homeFourthLineRightDefenceID, this.homeFourthLineRightDefence],
      [this.homeFifthLineLeftDefenceID, this.homeFifthLineLeftDefence],
      [this.homeFifthLineRightDefenceID, this.homeFifthLineRightDefence],
      [this.homeSixthLineLeftDefenceID, this.homeSixthLineLeftDefence],
      [this.homeSixthLineRightDefenceID, this.homeSixthLineRightDefence],
      [this.homeSeventhLineLeftDefenceID, this.homeSeventhLineLeftDefence],
      [this.homeSeventhLineRightDefenceID, this.homeSeventhLineRightDefence],
      [this.homeEighthLineLeftDefenceID, this.homeEighthLineLeftDefence],
      [this.homeEighthLineRightDefenceID, this.homeEighthLineRightDefence],
      [this.homeExtraLineDefenceID, this.homeExtraLineDefence],

      [this.homeFirstLineLeftForwardID, this.homeFirstLineLeftForward],
      [this.homeFirstLineCenterID, this.homeFirstLineCenter],
      [this.homeFirstLineRightForwardID, this.homeFirstLineRightForward],
      [this.homeSecondLineLeftForwardID, this.homeSecondLineLeftForward],
      [this.homeSecondLineCenterID, this.homeSecondLineCenter],
      [this.homeSecondLineRightForwardID, this.homeSecondLineRightForward],
      [this.homeThirdLineLeftForwardID, this.homeThirdLineLeftForward],
      [this.homeThirdLineCenterID, this.homeThirdLineCenter],
      [this.homeThirdLineRightForwardID, this.homeThirdLineRightForward],
      [this.homeFourthLineLeftForwardID, this.homeFourthLineLeftForward],
      [this.homeFourthLineCenterID, this.homeFourthLineCenter],
      [this.homeFourthLineRightForwardID, this.homeFourthLineRightForward],
      [this.homeFifthLineLeftForwardID, this.homeFifthLineLeftForward],
      [this.homeFifthLineCenterID, this.homeFifthLineCenter],
      [this.homeFifthLineRightForwardID, this.homeFifthLineRightForward],
      [this.homeSixthLineLeftForwardID, this.homeSixthLineLeftForward],
      [this.homeSixthLineCenterID, this.homeSixthLineCenter],
      [this.homeSixthLineRightForwardID, this.homeSixthLineRightForward],
      [this.homeSeventhLineLeftForwardID, this.homeSeventhLineLeftForward],
      [this.homeSeventhLineCenterID, this.homeSeventhLineCenter],
      [this.homeSeventhLineRightForwardID, this.homeSeventhLineRightForward],
      [this.homeEighthLineLeftForwardID, this.homeEighthLineLeftForward],
      [this.homeEighthLineCenterID, this.homeEighthLineCenter],
      [this.homeEighthLineRightForwardID, this.homeEighthLineRightForward],
      [this.homeExtraLineForwardID, this.homeExtraLineForward]
    ];
  }

  private getAwayTeamPlayersById(): [string, string][] {
    return [
      [this.awayGoalkeeperID, this.awayGoalkeeper],
      [this.awayBackupGoalkeeperID, this.awayBackupGoalkeeper],
      [this.awayFirstLineLeftDefenceID, this.awayFirstLineLeftDefence],
      [this.awayFirstLineRightDefenceID, this.awayFirstLineRightDefence],
      [this.awaySecondLineLeftDefenceID, this.awaySecondLineLeftDefence],
      [this.awaySecondLineRightDefenceID, this.awaySecondLineRightDefence],
      [this.awayThirdLineLeftDefenceID, this.awayThirdLineLeftDefence],
      [this.awayThirdLineRightDefenceID, this.awayThirdLineRightDefence],
      [this.awayFourthLineLeftDefenceID, this.awayFourthLineLeftDefence],
      [this.awayFourthLineRightDefenceID, this.awayFourthLineRightDefence],
      [this.awayFifthLineLeftDefenceID, this.awayFifthLineLeftDefence],
      [this.awayFifthLineRightDefenceID, this.awayFifthLineRightDefence],
      [this.awaySixthLineLeftDefenceID, this.awaySixthLineLeftDefence],
      [this.awaySixthLineRightDefenceID, this.awaySixthLineRightDefence],
      [this.awaySeventhLineLeftDefenceID, this.awaySeventhLineLeftDefence],
      [this.awaySeventhLineRightDefenceID, this.awaySeventhLineRightDefence],
      [this.awayEighthLineLeftDefenceID, this.awayEighthLineLeftDefence],
      [this.awayEighthLineRightDefenceID, this.awayEighthLineRightDefence],
      [this.awayExtraLineDefenceID, this.awayExtraLineDefence],

      [this.awayFirstLineLeftForwardID, this.awayFirstLineLeftForward],
      [this.awayFirstLineCenterID, this.awayFirstLineCenter],
      [this.awayFirstLineRightForwardID, this.awayFirstLineRightForward],
      [this.awaySecondLineLeftForwardID, this.awaySecondLineLeftForward],
      [this.awaySecondLineCenterID, this.awaySecondLineCenter],
      [this.awaySecondLineRightForwardID, this.awaySecondLineRightForward],
      [this.awayThirdLineLeftForwardID, this.awayThirdLineLeftForward],
      [this.awayThirdLineCenterID, this.awayThirdLineCenter],
      [this.awayThirdLineRightForwardID, this.awayThirdLineRightForward],
      [this.awayFourthLineLeftForwardID, this.awayFourthLineLeftForward],
      [this.awayFourthLineCenterID, this.awayFourthLineCenter],
      [this.awayFourthLineRightForwardID, this.awayFourthLineRightForward],
      [this.awayFifthLineLeftForwardID, this.awayFifthLineLeftForward],
      [this.awayFifthLineCenterID, this.awayFifthLineCenter],
      [this.awayFifthLineRightForwardID, this.awayFifthLineRightForward],
      [this.awaySixthLineLeftForwardID, this.awaySixthLineLeftForward],
      [this.awaySixthLineCenterID, this.awaySixthLineCenter],
      [this.awaySixthLineRightForwardID, this.awaySixthLineRightForward],
      [this.awaySeventhLineLeftForwardID, this.awaySeventhLineLeftForward],
      [this.awaySeventhLineCenterID, this.awaySeventhLineCenter],
      [this.awaySeventhLineRightForwardID, this.awaySeventhLineRightForward],
      [this.awayEighthLineLeftForwardID, this.awayEighthLineLeftForward],
      [this.awayEighthLineCenterID, this.awayEighthLineCenter],
      [this.awayEighthLineRightForwardID, this.awayEighthLineRightForward],
      [this.awayExtraLineForwardID, this.awayExtraLineForward]
    ];
  }

  getOfficialIds() {
    return this.officials
      .map((official) => official.officialId)
      .filter((id) => !!id);
  }

  getCVFailures() {
    return this.cvPipeline.filter((p) => p.lastStatus === 'FAILED');
  }

  getCVWarnings() {
    const warnings = this.cvPipeline
      ?.map((p) => p.warnings)
      .filter((w) => !!w)
      .flatMap((w) => w);
    return warnings ?? [];
  }

  findCVWarnings(period: string) {
    const warnings = this.cvPipeline
      .filter((w) => w.period === period)
      ?.map((p) => p.warnings)
      .filter((w) => !!w)
      .flatMap((w) => w);
    return warnings ?? [];
  }

  isCollectionLiteOrPlus(): boolean {
    return this.dataSet === DataSet.LITE || this.dataSet === DataSet.LITE_PLUS;
  }
}
